/* eslint-disable import/group-exports */

export type ThemeMode = "light" | "dark";

/** Base color palette shared between light and dark variants */
export interface BaseColorPalette {
  primary: string;
  secondary: string;
  tertiary: string;
  heading: string;
  subHeading: string;
  text: string;
  background: string;
  red: string;
  blue: string;
  white: string;
  borderLightGrey: string;
}

/** Full colors object with light/dark variants */
export const colors: Record<ThemeMode, BaseColorPalette> = {
  light: {
    primary: "#572148",
    secondary: "#FFF",
    tertiary: "#000",
    heading: "#572148",
    subHeading: "#572148",
    text: "#212529",
    background: "#F4F7FD",
    red: "#DC3545", // (Errors, some buttons & other places)
    blue: "#007bff", // links
    white: "#FFFFFF", // containers , starting page background
    borderLightGrey: "#e3e3e3" // borders (most) - tab, input ,side bar button, accordion border etc.
  },
  dark: {
    primary: "#572148",
    secondary: "#000",
    tertiary: "#FFF",
    heading: "#572148",
    subHeading: "#572148",
    text: "#F4F7FD",
    background: "#212529",
    red: "#DC3545", // (Errors, some buttons & other places)
    blue: "#007bff", // links
    white: "#FFFFFF", // containers , starting page background
    borderLightGrey: "#e3e3e3" // borders (most) - tab, input ,side bar button, accordion border etc.
  }
};

/** Base font definitions shared between light and dark variants */
export interface BaseFontSet {
  primary: string;
  secondary: string;
  tertiary: string;
  headings: string;
  eventCards: string;
  minimalistic: string;
}

/** Full fonts object with light/dark variants */
export const fonts: Record<ThemeMode, BaseFontSet> = {
  light: {
    primary: "Hind",
    secondary: "Nunito",
    tertiary: "Lato",
    headings: "Hind",
    eventCards: "Hind",
    minimalistic: "Hind"
  },
  dark: {
    primary: "Hind",
    secondary: "Nunito",
    tertiary: "Lato",
    headings: "Hind",
    eventCards: "Hind",
    minimalistic: "Hind"
  }
};

/** Dashboard‐specific colors that extend the base palette */
export interface DashboardColors extends BaseColorPalette {
  purple: string;
  grey: string;
  black: string;
  backgroundWhite: string;
  lightWhite: string;
  lightGrey: string;
  lightestPurple: string;
  borderGrey: string;
  lightGreen: string;
}

/** Full dashboardColors object with light/dark variants */
export const dashboardColors: Record<ThemeMode, DashboardColors> = {
  light: {
    ...colors.light,
    purple: "#572148", // Main Dashboard theme color
    grey: "#6c757d", // Nav Items, Subtitles, less important texts
    black: "#212529", // text color on AD
    backgroundWhite: "#F4F7FD", // background of the dashboard pages
    lightWhite: "#f5f5f0", // data table headers
    lightGrey: "#eae5e9", // NavItems
    lightestPurple: "#f1ddec", // Certain areas like Teams page box
    borderGrey: "#00000080", // border - darker border -> some rare places like forms
    lightGreen: "#cfebdd" // attendee count badge background
  },
  dark: {
    ...colors.dark,
    purple: "#572148", // Main Dashboard theme color
    grey: "#6c757d", // Nav Items, Subtitles, less important texts
    black: "#212529", // text color on AD
    backgroundWhite: "#F4F7FD", // background of the dashboard pages
    lightWhite: "#f5f5f0", // data table headers
    lightGrey: "#eae5e9", // NavItems
    lightestPurple: "#f1ddec", // Certain areas like Teams page box
    borderGrey: "#00000080", // border - darker border -> some rare places like forms
    lightGreen: "#cfebdd" // attendee count badge background
  }
};

/** Website‐specific colors that extend the base palette */
export interface WebsiteColorsLight extends BaseColorPalette {
  primaryWebsiteTheme: string;
  secondaryWebsiteTheme: string;
  headingColor: string;
  textColor: string;
  staticPagesHeading: string;
  staticPagesLink: string;
  staticPagesTableBorder: string;
  dotted: string;
  customCheckboxBorder: string;
  customCheckboxBackground: string;
  textParagraph: string;
  textSecondary: string;
  primaryRegistrationTheme: string;
  secondaryRegistrationTheme: string;
  borderColor: string;
  headingRegistrationColor: string;
  modalHeading: string;
}

export interface WebsiteColorsDark extends BaseColorPalette {
  primaryWebsiteTheme: string;
  secondaryWebsiteTheme: string;
  headingColor: string;
  textColor: string;
  staticPagesHeading: string;
  staticPagesLink: string;
  staticPagesTableBorder: string;
  dotted: string;
  customCheckboxBorder: string;
  customCheckboxBackground: string;
  textParagraph: string;
  textSecondary: string;
  primaryRegistrationTheme: string;
  secondaryRegistrationTheme: string;
  borderColor: string;
  headingRegistrationColor: string;
  modalHeading: string;
}

/** Full websiteColors object with light/dark variants */
export const websiteColors: {
  light: WebsiteColorsLight;
  dark: WebsiteColorsDark;
} = {
  light: {
    ...colors.light,
    primaryWebsiteTheme: "#002E6E",
    secondaryWebsiteTheme: "#E1F0FF",
    headingColor: "#101828",
    textColor: "#475467",
    staticPagesHeading: "#001234",
    staticPagesLink: "#006ef2",
    staticPagesTableBorder: "#61c0ff",
    dotted: "#BFBFBF",
    customCheckboxBorder: "#D0D5DD",
    customCheckboxBackground: "#fafbfd",
    textParagraph: "#292929",
    textSecondary: "#424242",
    primaryRegistrationTheme: "#002E6E",
    secondaryRegistrationTheme: "#1C3177",
    borderColor: "#D9D9D9",
    headingRegistrationColor: "#525252",
    modalHeading: "#001A47"
  },
  dark: {
    ...colors.dark,
    primaryWebsiteTheme: "#002E6E",
    secondaryWebsiteTheme: "#E1F0FF",
    headingColor: "#101828",
    textColor: "#475467",
    staticPagesHeading: "#001234",
    staticPagesLink: "#006ef2",
    staticPagesTableBorder: "#61c0ff",
    dotted: "#BFBFBF",
    customCheckboxBorder: "#D0D5DD",
    customCheckboxBackground: "#fafbfd",
    textParagraph: "#292929",
    textSecondary: "#424242",
    primaryRegistrationTheme: "#002E6E",
    secondaryRegistrationTheme: "#1C3177",
    borderColor: "#D9D9D9",
    headingRegistrationColor: "#525252",
    modalHeading: "#001A47"
  }
};

/** Dashboard font sets simply mirror the base fonts */
export const dashboardFonts: Record<ThemeMode, BaseFontSet> = {
  light: { ...fonts.light },
  dark: { ...fonts.dark }
};

/** Website‐specific font sets extending the base fonts */
export interface WebsiteFontSet extends BaseFontSet {
  minimalistic: string;
  eventCards: string;
  konfhub: string;
  konfhubWebsiteHeadings: string;
}

/** Full websiteFonts object with light/dark variants */
export const websiteFonts: Record<ThemeMode, WebsiteFontSet> = {
  light: {
    ...fonts.light,
    minimalistic: "Montserrat",
    eventCards: "Space Grotesk",
    konfhub: "Caveat Brush",
    konfhubWebsiteHeadings: "Poppins"
  },
  dark: {
    ...fonts.dark,
    minimalistic: "Montserrat",
    eventCards: "Space Grotesk",
    konfhub: "Caveat Brush",
    konfhubWebsiteHeadings: "Poppins"
  }
};

/** Current theme mode; can be toggled at runtime */
export let theme: ThemeMode = "light";
